import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? match[1] : null;

    const run = async () => {
      if (!sessionId) {
        navigate("/", { replace: true });
        return;
      }
      try {
        const referredBy = localStorage.getItem("pending_referred_by") || undefined;
        // Lazy Registration handoff — the guest identity lives in a server-issued
        // HttpOnly signed cookie set by /api/guest/session. The backend reads
        // it directly on /api/auth/session, atomically marks the session as
        // converted, transfers the guest render into the new account, and
        // grants the remaining promo credits. No client-supplied guest id.
        const res = await api.post("/auth/session", { session_id: sessionId, referred_by: referredBy });
        if (referredBy) localStorage.removeItem("pending_referred_by");
        // Clean up the legacy client-generated guest id (previous funnel).
        try { localStorage.removeItem("flyerstudio_guest_id"); } catch (_) { /* no-op */ }
        if (res.data.session_token) {
          localStorage.setItem("session_token", res.data.session_token);
        }
        // GA4 — fire signup_completed exactly once when the backend confirms a
        // NEW user account was created. `is_new` is authoritative and only true
        // on the initial signup; it will be false/undefined on all subsequent
        // sign-ins for the same account, so this event cannot double-fire.
        if (res.data?.is_new === true && typeof window !== "undefined") {
          const params = { method: "google" };
          if (typeof window.gtag === "function") {
            window.gtag("event", "signup_completed", params);
          } else if (Array.isArray(window.dataLayer)) {
            window.dataLayer.push(["event", "signup_completed", params]);
          }
        }
        setUser(res.data.user);
        // Resume a pending checkout the user started before signing in.
        const pendingPlan = localStorage.getItem("pending_plan_id");
        const pendingTopup = localStorage.getItem("pending_topup");
        if (pendingPlan) {
          localStorage.removeItem("pending_plan_id");
          try {
            const r = await api.post("/payments/checkout", { plan_id: pendingPlan, origin_url: window.location.origin });
            if (r.data.url) { window.location.href = r.data.url; return; }
          } catch (e) { /* fall through to dashboard */ }
        } else if (pendingTopup) {
          localStorage.removeItem("pending_topup");
          try {
            const r = await api.post("/billing/topup", { pack_id: pendingTopup, origin_url: window.location.origin });
            if (r.data.url) { window.location.href = r.data.url; return; }
          } catch (e) { /* fall through to dashboard */ }
        }
        // Lazy Registration: if this user was mid-flow in AI Pro Studio when
        // they signed up (Download / Rerun / 2nd-Generate intercepted → modal →
        // Google), hand them straight back to the studio with a resume flag
        // so the pending action can execute automatically. The guest_id is
        // kept in localStorage until AiProStudio consumes it on mount.
        const guestPendingAction = localStorage.getItem("flyerstudio_guest_pending_action");
        if (guestPendingAction) {
          navigate("/ai-pro-studio?resume=1", { replace: true });
          return;
        }
        navigate("/dashboard", { replace: true });
      } catch (e) {
        navigate("/", { replace: true });
      }
    };
    run();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white">
      <div className="flex flex-col items-center gap-4">
        <div className="h-9 w-9 rounded-full border-2 border-white/20 border-t-blue-500 animate-spin" />
        <p className="text-sm text-zinc-400 font-display tracking-wide">Signing you in…</p>
      </div>
    </div>
  );
}
